import React, { useState, useEffect } from "react";
import { getCateringCalendarView } from "../../api/get/getCateringCalendarViewApi";
import { getCateringOrderDetailsApi } from "../../api/get/getCateringOrderDetailsApi";
import CateringOrderDetailsModal from "../../components/modal/CateringOrderDetailsModal";

export const CateringCalendarView = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11

  // Fetch calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCateringCalendarView(
          currentYear,
          currentMonth + 1 // API expects 1-12
        );
        setCalendarData(data.data);
      } catch (err) {
        setError("Failed to load calendar data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarData();
  }, [currentYear, currentMonth]);

  // Navigation handlers
  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  // Transform API response to match modal's expected format
  const transformOrderDetails = (apiData) => {
    if (!apiData) return null;

    // Handle both customer and company addresses
    let addressDetails = null;
    
    if (apiData.userType === "customer" && apiData.user_address) {
      // Customer address
      const addr = apiData.user_address;
      addressDetails = {
        addressType: addr.address_type,
        block: addr.block,
        street: addr.street,
        avenue: addr.avenue,
        houseNo: addr.house_no,
        building: addr.building,
        floor: addr.floor,
        apartmentNo: addr.apartment_no,
        companyName: addr.company_name,
        mobileNumber: addr.mobile_number,
        countryCode: addr.country_code,
      };
    } else if (apiData.userType === "company" && apiData.customerAddress) {
      // Company address
      const addr = apiData.customerAddress;
      addressDetails = {
        address1: addr.address1,
        address2: addr.address2,
      };
    }

    return {
      orderNo: apiData.order_no,
      restaurant: apiData.restaurant_name_en,
      serviceItemName: apiData.service_item_name_en,
      customerName: `${apiData.customer_first_name || ''} ${apiData.customer_last_name || ''}`.trim(),
      orderDate: apiData.order_date,
      serviceItemDate: apiData.service_item_date,
      serviceItemSlot: apiData.service_item_slot,
      noOfPax: apiData.no_of_pax,
      orderStatus: apiData.order_status,
      paymentMethod: apiData.payment_method_en,
      updatedByAgentId: apiData.updated_by_agent_id || "",
      updatedDatetime: apiData.updated_datetime || "",
      serviceItems: apiData.service_item || [],
      notes: apiData.special_instructions_1 || apiData.special_instructions_2 || apiData.comments || "",
      service_item_price: apiData.service_item_price,
      order_total_price: apiData.order_total_price,
      total_price: apiData.order_total_price,
      currency: apiData.currency,
      paymentProcessingFee: apiData.payment_processing_fee || 0,
      userType: apiData.userType,
      addressDetails: addressDetails,
    };
  };

  // Handle event click - fetch order details and show modal
  const handleEventClick = async (orderId) => {
    if (!orderId) return;
    
    setLoadingOrderDetails(true);
    try {
      const response = await getCateringOrderDetailsApi(orderId);
      console.log("API Response:", response);
      
      // Handle both response structures: response.data or direct response
      const orderData = response?.data || response;
      console.log("Order Data:", orderData);
      
      if (orderData) {
        const transformedData = transformOrderDetails(orderData);
        console.log("Transformed Data:", transformedData);
        setSelectedOrderDetails(transformedData);
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error("Failed to load order details:", err);
      // Optionally show an error message to the user
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderDetails(null);
  };

  // Extract and format time from eventTime string
  const formatTime = (eventTimeString) => {
    // The eventTime comes as "2025-10-12T11:00 AM - 11:30 AMZ"
    // We want to extract just the time portion: "11:00 AM - 11:30 AM"
    if (!eventTimeString) return "";
    
    // Remove the "Z" at the end if present
    const cleanedTime = eventTimeString.replace(/Z$/, "");
    
    // Extract the time part after the date
    const timePart = cleanedTime.split("T")[1];
    return timePart || cleanedTime;
  };

  // Parse event time for sorting (extract first time from range)
  const getEventSortTime = (eventTimeString) => {
    if (!eventTimeString) return 0;
    
    // Extract date and first time from string like "2025-10-12T11:00 AM - 11:30 AMZ"
    const dateMatch = eventTimeString.match(/(\d{4}-\d{2}-\d{2})/);
    const timeMatch = eventTimeString.match(/T(\d{1,2}:\d{2}\s*(?:AM|PM))/i);
    
    if (dateMatch && timeMatch) {
      const dateStr = dateMatch[1];
      const timeStr = timeMatch[1].trim();
      
      // Convert to 24-hour format for proper sorting
      const [time, period] = timeStr.split(/\s+/);
      const [hours, minutes] = time.split(':').map(Number);
      let adjustedHours = hours;
      
      if (period && period.toUpperCase() === 'PM' && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (period && period.toUpperCase() === 'AM' && hours === 12) {
        adjustedHours = 0;
      }
      
      return new Date(`${dateStr}T${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`).getTime();
    }
    
    return 0;
  };

  // Get calendar grid
  const getCalendarGrid = () => {
    if (!calendarData || !calendarData.days) return [];

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startDay = firstDayOfMonth.getDay(); // 0 (Sunday) to 6 (Saturday)
    const daysInMonth = lastDayOfMonth.getDate();

    // Create a map of dates to events
    const dateEventsMap = {};
    calendarData.days.forEach((day) => {
      dateEventsMap[day.date] = day;
    });

    // Build calendar grid
    const grid = [];
    let currentWeek = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startDay; i++) {
      currentWeek.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${currentYear}-${String(currentMonth + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;
      const dayData = dateEventsMap[dateString];

      currentWeek.push({
        date: day,
        dateString,
        dayData,
      });

      // If we've filled a week (7 days), push it to grid and start new week
      if (currentWeek.length === 7) {
        grid.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add remaining days to complete the last week
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      grid.push(currentWeek);
    }

    return grid;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return (
    <div className="p-4 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Catering Events Calendar</h1>
        <button
          onClick={goToCurrentMonth}
          className="px-4 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition"
        >
          Today
        </button>
      </div>

      {/* Month Navigation */}
      <div className="mb-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
        <button
          onClick={handlePreviousMonth}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
        >
          ← Previous
        </button>
        <h2 className="text-2xl font-bold text-gray-700">
          {monthNames[currentMonth]} {currentYear}
        </h2>
        <button
          onClick={handleNextMonth}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
        >
          Next →
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800"></div>
          <p className="mt-2 text-gray-600">Loading calendar...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Calendar Grid */}
      {!loading && !error && calendarData && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Day Names Header */}
          <div className="grid grid-cols-7 bg-gray-200 border-b">
            {dayNames.map((day) => (
              <div
                key={day}
                className="py-3 text-center font-bold text-gray-700 text-sm border-r last:border-r-0"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          {getCalendarGrid().map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 border-b last:border-b-0">
              {week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className="border-r last:border-r-0 min-h-[150px] p-2 bg-white hover:bg-gray-50 transition"
                >
                  {day && (
                    <>
                      {/* Day Number */}
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm font-semibold text-gray-600">
                          {day.date}
                        </span>
                      </div>

                      {/* Event Count - Prominently displayed */}
                      {day.dayData && day.dayData.events && day.dayData.events.length > 0 && (
                        <div className="mb-2 bg-amber-800 text-white text-center py-1 px-2 rounded font-bold text-sm">
                          {day.dayData.events.length} {day.dayData.events.length === 1 ? 'Event' : 'Events'}
                        </div>
                      )}

                      {/* Events List */}
                      {day.dayData && day.dayData.events && (
                        <div className="space-y-1 max-h-[120px] overflow-y-auto">
                          {day.dayData.events
                            .sort(
                              (a, b) =>
                                getEventSortTime(a.eventTime) -
                                getEventSortTime(b.eventTime)
                            )
                            .map((event) => (
                              <div
                                key={event.id}
                                onClick={() => handleEventClick(event.order_id)}
                                className={`text-xs p-2 rounded cursor-pointer transition-all hover:shadow-md ${
                                  event.status === "paid"
                                    ? "bg-green-100 border border-green-300 hover:bg-green-200"
                                    : "bg-green-100 border border-green-300 hover:bg-green-200"
                                }`}
                              >
                                <div className="font-semibold text-green-800 mb-1">
                                  {formatTime(event.eventTime)}
                                </div>
                                <div className="text-gray-700 truncate" title={event.eventType}>
                                  {event.eventType}
                                </div>
                                <div className="text-gray-600 truncate" title={event.customerName}>
                                  {event.customerName}
                                </div>
                                {event.mobileNumber && (
                                  <div className="text-gray-500 truncate">
                                    {event.mobileNumber}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Loading Indicator for Order Details */}
      {loadingOrderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800 mb-4"></div>
            <p className="text-gray-700 font-medium">Loading order details...</p>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      <CateringOrderDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        orderDetails={selectedOrderDetails}
      />
    </div>
  );
};

